import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Toolbar,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  Chip,
  CircularProgress,
  ListSubheader,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { format } from 'date-fns';
import { Query, Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import { withSnackbar } from 'notistack';
import InventoryUserAutocomplete from '../common/InventoryUserAutocomplete';
import QuickNoteDetailModal from './QuickNoteDetailModal';
import QuickNoteFormModal from './QuickNoteFormModal';
import DeleteQuickNoteDialog from './DeleteQuickNoteDialog';
import EditQuickNoteDialog from './EditQuickNoteDialog';

// GraphQL query to get Quick Notes with filtering and pagination
const GET_QUICK_NOTES = gql`
  query GetQuickNotes(
    $limit: Int
    $offset: Int
    $order_by: String
    $order: String
    $inventory_user_id: ID
    $category_id: ID
    $activity_type_id: ID
    $date_from: String
    $date_to: String
    $created_by_user_id: ID
    $planned: Boolean
  ) {
    getQuickNotes(
      limit: $limit
      offset: $offset
      order_by: $order_by
      order: $order
      inventory_user_id: $inventory_user_id
      category_id: $category_id
      activity_type_id: $activity_type_id
      date_from: $date_from
      date_to: $date_to
      created_by_user_id: $created_by_user_id
      planned: $planned
    ) {
      id
      comment
      time_spent
      time_spent_formatted
      interaction_date
      created_at
      planned
      planned_status_text
      user {
        id
        name
        allowed_name
      }
      commentable {
        id
        name
        branch
      }
      activityType {
        id
        name
      }
      quickNoteCategory {
        id
        name
      }
      all_categories {
        id
        name
      }
      documents {
        id
        name
        file_size
        mime_type
        download_url
      }
      pipeline_card {
        id
        name
        pipeline {
          id
          name
        }
      }
    }
    getQuickNotesCount(
      inventory_user_id: $inventory_user_id
      category_id: $category_id
      activity_type_id: $activity_type_id
      date_from: $date_from
      date_to: $date_to
      created_by_user_id: $created_by_user_id
      planned: $planned
    )
  }
`;

// GraphQL mutation to delete a Quick Note
const DELETE_QUICK_NOTE = gql`
  mutation DeleteQuickNote($id: ID!) {
    deleteQuickNote(id: $id)
  }
`;

const styles = theme => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  title: {
    flex: '1 1 100%',
  },
  filtersSection: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  actionButtons: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    '& > *': {
      marginLeft: theme.spacing(1),
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
  },
  tableContainer: {
    maxHeight: 600,
  },
  commentCell: {
    maxWidth: 300,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  chip: {
    margin: theme.spacing(0.5),
  },
});

class QuickNotes extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      // Pagination
      page: 0,
      rowsPerPage: 25,
      
      // Sorting
      orderBy: 'interaction_date',
      order: 'desc',
      
      // Filters
      selectedInventoryUser: null,
      selectedCategory: '',
      selectedActivityType: '',
      selectedCreatedByUser: '',
      dateFrom: null,
      dateTo: null,
      selectedPlannedStatus: '',

      // Modal
      isModalOpen: false,
      selectedNote: null,
      isFormModalOpen: false,
      isDeleteDialogOpen: false,
      noteToDelete: null,
      isEditDialogOpen: false,
      noteToEdit: null,
      
      // URL parameter handling
      quickNoteIdFromUrl: null,
    };
  }

  componentDidMount() {
    // Check for quick_note_id in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const quickNoteId = urlParams.get('quick_note_id');
    
    if (quickNoteId) {
      this.setState({ quickNoteIdFromUrl: quickNoteId });
    }
  }

  // Method to open modal when note is found
  openNoteModal = (notes) => {
    const { quickNoteIdFromUrl } = this.state;
    
    if (quickNoteIdFromUrl && notes && notes.length > 0) {
      const note = notes.find(n => n.id === quickNoteIdFromUrl);
      if (note) {
        this.setState({
          isModalOpen: true,
          selectedNote: note,
          quickNoteIdFromUrl: null, // Clear so it doesn't open again
        });
        
        // Clean up URL
        const url = new URL(window.location);
        url.searchParams.delete('quick_note_id');
        window.history.replaceState({}, '', url);
      }
    }
  }

  // Helper function to clean category names for display
  cleanCategoryName = (categoryName) => {
    if (!categoryName) return '';
    const cleaned = categoryName
      .replace('Practice Exchange   ', '')
      .replace('Succession Planning   ', '');
    
    // Capitalize 3-letter words (like rsp, erd, etc.)
    return cleaned.split(' ').map(word => 
      word.length === 3 ? word.toUpperCase() : word
    ).join(' ');
  };

  handleRowClick = (note) => {
    this.setState({
      isModalOpen: true,
      selectedNote: note,
    });
  };

  handleCloseModal = () => {
    this.setState({
      isModalOpen: false,
      selectedNote: null,
    });
  };

  handleOpenFormModal = () => {
    this.setState({ isFormModalOpen: true });
  };

  handleCloseFormModal = () => {
    this.setState({ isFormModalOpen: false });
  };

  handleOpenDeleteDialog = (note, event) => {
    event.stopPropagation(); // Prevent row click event
    this.setState({
      isDeleteDialogOpen: true,
      noteToDelete: note,
    });
  };

  handleCloseDeleteDialog = () => {
    this.setState({
      isDeleteDialogOpen: false,
      noteToDelete: null,
    });
  };

  handleOpenEditDialog = (note, event) => {
    event.stopPropagation(); // Prevent row click event
    this.setState({
      isEditDialogOpen: true,
      noteToEdit: note,
    });
  };

  handleCloseEditDialog = () => {
    this.setState({
      isEditDialogOpen: false,
      noteToEdit: null,
    });
  };

  handleEditSuccess = (refetch) => (updatedNote) => {
    this.handleCloseEditDialog();
    // Refetch the notes to update the table
    if (refetch) {
      refetch(this.getQueryVariables());
    }
  };

  handleDeleteNote = (deleteNoteMutation, refetch) => async () => {
    const { noteToDelete } = this.state;
    const { enqueueSnackbar } = this.props;

    if (!noteToDelete) return;

    try {
      await deleteNoteMutation({
        variables: { id: noteToDelete.id },
      });

      enqueueSnackbar('Quick Note successfully deleted', { variant: 'success' });
      this.handleCloseDeleteDialog();
      
      // Refetch the data
      refetch(this.getQueryVariables());
    } catch (error) {
      enqueueSnackbar(`Error deleting Quick Note: ${error.message}`, { variant: 'error' });
    }
  };

  handleApplyFilters = (refetch) => () => {
    this.setState({ page: 0 }, () => refetch(this.getQueryVariables()));
  };

  handleChangePage = (refetch) => (event, newPage) => {
    this.setState({ page: newPage }, () => refetch(this.getQueryVariables()));
  };

  handleChangeRowsPerPage = (refetch) => (event) => {
    this.setState(
      { 
        rowsPerPage: parseInt(event.target.value, 10),
        page: 0 
      }, 
      () => refetch(this.getQueryVariables())
    );
  };

  handleRequestSort = (refetch) => (property) => {
    const isAsc = this.state.orderBy === property && this.state.order === 'asc';
    this.setState(
      {
        order: isAsc ? 'desc' : 'asc',
        orderBy: property,
        page: 0,
      }, 
      () => refetch(this.getQueryVariables())
    );
  };

  handleFilterChange = (filterName) => (value) => {
    this.setState({
      [filterName]: value,
    });
  };

  handleInventoryUserChange = (user) => {
    this.setState({
      selectedInventoryUser: user,
    });
  };

  clearFilters = (refetch) => () => {
    this.setState(
      {
        selectedInventoryUser: null,
        selectedCategory: '',
        selectedActivityType: '',
        selectedCreatedByUser: '',
        dateFrom: null,
        dateTo: null,
        selectedPlannedStatus: '',
        page: 0,
      }, 
      () => refetch(this.getQueryVariables())
    );
  };

  getQueryVariables = () => {
    const { page, rowsPerPage, orderBy, order, selectedInventoryUser, selectedCategory, selectedActivityType, selectedCreatedByUser, dateFrom, dateTo, selectedPlannedStatus, quickNoteIdFromUrl } = this.state;
    
    // If looking for specific note from URL, fetch more results to find it
    const effectiveLimit = quickNoteIdFromUrl ? 1000 : rowsPerPage;
    const effectiveOffset = quickNoteIdFromUrl ? 0 : (page * rowsPerPage);
    
    return {
      limit: effectiveLimit,
      offset: effectiveOffset,
      order_by: orderBy,
      order,
      inventory_user_id: selectedInventoryUser?.id || null,
      category_id: selectedCategory || null,
      activity_type_id: selectedActivityType || null,
      created_by_user_id: selectedCreatedByUser || null,
      date_from: dateFrom ? format(dateFrom, 'yyyy-MM-dd') : null,
      date_to: dateTo ? format(dateTo, 'yyyy-MM-dd') : null,
      planned: selectedPlannedStatus === '' ? null : selectedPlannedStatus === 'true',
    };
  };

  handleExportCSV = () => {
    const queryParams = new URLSearchParams();
    const { selectedInventoryUser, selectedCategory, selectedActivityType, selectedCreatedByUser, dateFrom, dateTo, selectedPlannedStatus } = this.state;
    
    if (selectedInventoryUser) queryParams.append('inventory_user_id', selectedInventoryUser.id);
    if (selectedCategory) queryParams.append('category_id', selectedCategory);
    if (selectedActivityType) queryParams.append('activity_type_id', selectedActivityType);
    if (selectedCreatedByUser) queryParams.append('created_by_user_id', selectedCreatedByUser);
    if (dateFrom) queryParams.append('date_from', dateFrom.toISOString().split('T')[0]);
    if (dateTo) queryParams.append('date_to', dateTo.toISOString().split('T')[0]);
    if (selectedPlannedStatus) queryParams.append('planned', selectedPlannedStatus === 'true');
    
    const exportUrl = `/admin/quick_notes.csv?${queryParams.toString()}`;
    window.open(exportUrl, '_blank');
  };

  renderFilters(refetch) {
    const { classes, activityTypes, categories, authors } = this.props;
    const {
      orderBy,
      order,
      selectedCategory,
      selectedActivityType,
      selectedCreatedByUser,
      dateFrom,
      dateTo,
      selectedInventoryUser,
      selectedPlannedStatus,
    } = this.state;

    // Process categories to remove prefixes for display
    const processedCategories = categories.map(category => {
      const cleaned = category.name
        .replace('Practice Exchange   ', '')
        .replace('Succession Planning   ', '');
      
      // Capitalize 3-letter words (like rsp, erd, etc.)
      const displayName = cleaned.split(' ').map(word => 
        word.length === 3 ? word.toUpperCase() : word
      ).join(' ');
      
      return {
        ...category,
        displayName,
        section: category.name.startsWith('Practice Exchange   ') ? 'Practice Exchange' :
                category.name.startsWith('Succession Planning   ') ? 'Succession Planning' : 'Other'
      };
    });
    
    // Group by section
    const practiceExchange = processedCategories.filter(c => c.section === 'Practice Exchange');
    const successionPlanning = processedCategories.filter(c => c.section === 'Succession Planning');
    const other = processedCategories.filter(c => c.section === 'Other');
    
    return (
      <div className={classes.filtersSection}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth style={{ zIndex: 1301 }}>
              <InventoryUserAutocomplete
                value={selectedInventoryUser}
                onChange={this.handleInventoryUserChange}
                placeholder="Search by name or branch..."
                textFieldProps={{
                  label: 'Filter by Inventory User',
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => this.handleFilterChange('selectedCategory')(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                
                {practiceExchange.length > 0 && (
                  <ListSubheader disableSticky>Practice Exchange</ListSubheader>
                )}
                {practiceExchange.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.displayName}
                  </MenuItem>
                ))}
                
                {successionPlanning.length > 0 && (
                  <ListSubheader disableSticky>Succession Planning</ListSubheader>
                )}
                {successionPlanning.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.displayName}
                  </MenuItem>
                ))}
                
                {other.length > 0 && (
                  <ListSubheader disableSticky>Other</ListSubheader>
                )}
                {other.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Activity Type</InputLabel>
              <Select
                value={selectedActivityType}
                onChange={(e) => this.handleFilterChange('selectedActivityType')(e.target.value)}
              >
                <MenuItem value="">All Activity Types</MenuItem>
                {activityTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Author</InputLabel>
              <Select
                value={selectedCreatedByUser}
                onChange={(e) => this.handleFilterChange('selectedCreatedByUser')(e.target.value)}
              >
                <MenuItem value="">All Authors</MenuItem>
                {authors.map((author) => (
                  <MenuItem key={author.id} value={author.id}>
                    {author.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Row 2 */}
          <Grid item xs={12} sm={6} md={4}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                fullWidth
                label="From Date"
                format="yyyy-MM-dd"
                value={dateFrom}
                onChange={(date) => this.handleFilterChange('dateFrom')(date)}
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                fullWidth
                label="To Date"
                format="yyyy-MM-dd"
                value={dateTo}
                onChange={(date) => this.handleFilterChange('dateTo')(date)}
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={selectedPlannedStatus}
                onChange={(e) => this.handleFilterChange('selectedPlannedStatus')(e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="true">Planned</MenuItem>
                <MenuItem value="false">Unplanned</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box className={classes.actionButtons}>
            <Button 
              variant="contained" 
              color="secondary"
              onClick={this.handleOpenFormModal}
              style={{ marginRight: 'auto' }}
            >
              New Note
            </Button>
            <Button variant="outlined" onClick={this.clearFilters(refetch)}>
              Clear Filters
            </Button>
            <Button variant="contained" color="primary" onClick={this.handleApplyFilters(refetch)}>
              Apply Filters
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={this.handleExportCSV}
            >
              Export CSV
            </Button>
        </Box>
      </div>
    );
  }

  renderTableHead(refetch) {
    const { order, orderBy } = this.state;
    
    const headCells = [
      { id: 'created_at', label: 'Date Created', sortable: true },
      { id: 'interaction_date', label: 'Interaction Date', sortable: true },
      { id: 'user', label: 'User', sortable: false },
      { id: 'commentable', label: 'Inventory User', sortable: false },
      { id: 'branch', label: 'Branch', sortable: false },
      { id: 'pipeline_card', label: 'Linked Case/Deal', sortable: false },
      { id: 'quick_note_category', label: 'Categories', sortable: false },
      { id: 'activity_type', label: 'Activity Type', sortable: false },
      { id: 'time_spent', label: 'Time Spent', sortable: true },
      { id: 'planned', label: 'Type', sortable: true },
      { id: 'attachments', label: 'Attachment', sortable: false },
      { id: 'comment', label: 'Notes', sortable: false },
      { id: 'actions', label: 'Actions', sortable: false },
    ];

    return (
      <TableHead>
        <TableRow>
          {headCells.map((headCell) => (
            <TableCell
              key={headCell.id}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {headCell.sortable ? (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={() => this.handleRequestSort(refetch)(headCell.id)}
                >
                  {headCell.label}
                </TableSortLabel>
              ) : (
                headCell.label
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }

  render() {
    const { classes } = this.props;
    const { page, rowsPerPage, isModalOpen, selectedNote, isFormModalOpen, isDeleteDialogOpen, noteToDelete, isEditDialogOpen, noteToEdit } = this.state;

    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <Toolbar className={classes.toolbar} />
          
          <Mutation mutation={DELETE_QUICK_NOTE}>
            {(deleteNoteMutation) => (
              <Query query={GET_QUICK_NOTES} variables={this.getQueryVariables()} notifyOnNetworkStatusChange>
                {({ loading, error, data, refetch }) => {
              if (loading && !data?.getQuickNotes) {
                return (
                  <div className={classes.loadingContainer}>
                    <CircularProgress />
                  </div>
                );
              }
              
              if (error) {
                return (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <Typography color="error">
                      Error loading Quick Notes: {error.message}
                    </Typography>
                  </div>
                );
              }

              const quickNotes = data?.getQuickNotes || [];
              const totalCount = data?.getQuickNotesCount || 0;

              // Check if we need to open a specific note from URL
              if (this.state.quickNoteIdFromUrl && quickNotes.length > 0) {
                this.openNoteModal(quickNotes);
              }

              return (
                <>
                  {this.renderFilters(refetch)}
                  <TableContainer className={classes.tableContainer}>
                    <Table stickyHeader>
                      {this.renderTableHead(refetch)}
                      <TableBody>
                        {quickNotes.map((note) => (
                          <TableRow 
                            key={note.id} 
                            hover 
                            onClick={() => this.handleRowClick(note)}
                            style={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              {new Date(note.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              {note.interaction_date}
                            </TableCell>
                            <TableCell>
                              {note.user?.name}
                            </TableCell>
                            <TableCell>
                              {note.commentable?.name}
                            </TableCell>
                            <TableCell>
                              {note.commentable?.branch}
                            </TableCell>
                            <TableCell>
                              {note.pipeline_card ? (
                                <Box>
                                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                                    {note.pipeline_card.name}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {note.pipeline_card.pipeline?.name}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="textSecondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {note.all_categories && note.all_categories.length > 0 ? (
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                  {note.all_categories.map((category) => (
                                    <Chip 
                                      key={category.id}
                                      label={this.cleanCategoryName(category.name)} 
                                      size="small" 
                                      className={classes.chip}
                                    />
                                  ))}
                                </Box>
                              ) : note.quickNoteCategory?.name && (
                                <Chip 
                                  label={this.cleanCategoryName(note.quickNoteCategory.name)} 
                                  size="small" 
                                  className={classes.chip}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {note.activityType?.name && (
                                <Chip 
                                  label={note.activityType.name} 
                                  size="small" 
                                  variant="outlined"
                                  className={classes.chip}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {note.time_spent_formatted}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={note.planned_status_text || (note.planned ? 'Planned' : 'Unplanned')} 
                                size="small" 
                                variant={note.planned ? 'default' : 'outlined'}
                                color={note.planned ? 'primary' : 'default'}
                                className={classes.chip}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {note.documents && note.documents.length > 0 && (
                                <AttachFileIcon color="action" fontSize="small" />
                              )}
                            </TableCell>
                            <TableCell className={classes.commentCell}>
                              {note.comment}
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Edit Note">
                                <IconButton
                                  size="small"
                                  onClick={(event) => this.handleOpenEditDialog(note, event)}
                                  aria-label="edit note"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={(event) => this.handleOpenDeleteDialog(note, event)}
                                  aria-label="delete"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                        {quickNotes.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={12} align="center">
                              <Typography variant="body2" color="textSecondary">
                                No Quick Notes found with the current filters.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={this.handleChangePage(refetch)}
                    onRowsPerPageChange={this.handleChangeRowsPerPage(refetch)}
                  />
                  
                  <QuickNoteFormModal
                    open={isFormModalOpen}
                    onClose={this.handleCloseFormModal}
                    onNoteAdded={() => refetch(this.getQueryVariables())}
                  />

                  {selectedNote && (
                    <QuickNoteDetailModal
                      open={isModalOpen}
                      onClose={this.handleCloseModal}
                      note={selectedNote}
                    />
                  )}

                  <DeleteQuickNoteDialog
                    open={isDeleteDialogOpen}
                    onClose={this.handleCloseDeleteDialog}
                    onConfirm={this.handleDeleteNote(deleteNoteMutation, refetch)}
                    note={noteToDelete}
                  />

                  {noteToEdit && (
                    <EditQuickNoteDialog
                      open={isEditDialogOpen}
                      note={noteToEdit}
                      onClose={this.handleCloseEditDialog}
                      onSuccess={this.handleEditSuccess(refetch)}
                    />
                  )}
                </>
              );
            }}
          </Query>
            )}
          </Mutation>
        </Paper>
      </div>
    );
  }
}

QuickNotes.propTypes = {
  classes: PropTypes.object.isRequired,
  activityTypes: PropTypes.array.isRequired,
  categories: PropTypes.array.isRequired,
  authors: PropTypes.array.isRequired,
  currentUser: PropTypes.object,
};

QuickNotes.defaultProps = {
  activityTypes: [],
  categories: [],
  authors: [],
};

export default withStyles(styles)(withSnackbar(QuickNotes)); 